import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getTimeWidthInMilliSeconds } from '../../chartHelpers'
import { istToUTC, toDayFormat } from '../../../../util/dateTime'

export type DateRangeDrawingState = {
    p1?: Point,
    p2?: Point,
    p3?: Point,
    p4?: Point
}

export type DateRangeDrawingSettings = {
    fillColor: ColorValue,
    strokeColor: ColorValue,
    strokeWidth: number,
    labelColor: ColorValue
}


class DateRangeDrawingTool extends DrawingBase<any, DateRangeDrawingSettings, DateRangeDrawingState> {

    days = 0
    hours = 0
    minutes = 0
    seconds = 0
    bars = 0

    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
        p3x: 0,
        p3y: 0,
        p4x: 0,
        p4y: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context

        this.calculateAttributes()
        const { p1x, p1y, p2x, p2y, p3x, p3y, p4x, p4y } = this.scaledCoordinates

        const minX = Math.min(p1x, p2x), maxX = Math.max(p1x, p2x)
        const minY = Math.min(p1y, p2y), maxY = Math.max(p1y, p2y)
        const width = maxX - minX, height = maxY - minY
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fillStyle = this._settings.fillColor.color
        ctx.fillRect(minX, minY, width, height)

        ctx.beginPath()
        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p3x, p3y)
        ctx.moveTo(p2x, p2y)
        ctx.lineTo(p4x, p4y)

        ctx.moveTo(p1x, (p1y + p2y) / 2)
        ctx.lineTo(p2x, (p1y + p2y) / 2)
        ctx.globalAlpha = this._settings.strokeColor.opacity
        ctx.strokeStyle = this._settings.strokeColor.color
        ctx.lineWidth = this._settings.strokeWidth * window.devicePixelRatio
        ctx.stroke()
        ctx.closePath()

        this.drawStatsText(ctx)

    };

    drawStatsText(ctx: CanvasRenderingContext2D) {

        const { p1x, p1y, p2x, p2y } = this.scaledCoordinates

        let text = `${this.bars} bars${this.bars ? ',' : ''}`

        if (this.days) {
            text += ` ${this.days}d`
        }

        if (this.hours) {
            text += ` ${this.hours}h`
        }

        if (this.minutes) {
            text += ` ${this.minutes}m`
        }

        if (this.seconds) {
            text += ` ${this.seconds}s`
        }

        ctx.globalAlpha = 1
        const fontSize = 12 * window.devicePixelRatio
        ctx.font = fontSize + 'px sans-serif'
        ctx.textBaseline = 'top'
        let dim = ctx.measureText(text)
        const textHeight = dim.fontBoundingBoxAscent + dim.fontBoundingBoxDescent
        const textWidth = dim.width

        const startX = (p1x + p2x) / 2 - textWidth / 2
        const startY = Math.max(p1y, p2y) + 8
        ctx.beginPath()
        ctx.fillStyle = '#fff'
        ctx.roundRect(startX - 4, startY - 4, textWidth + 8, textHeight + 8, 2)
        ctx.closePath()
        ctx.fill()
        ctx.fillStyle = '#646464'
        ctx.fillText(text, startX, startY)
    }


    calculateAttributes() {
        const { p1, p2, p3, p4 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
        }

        if (p3 && p4) {
            let p3x = this.timeToCoordinate(p3.x)
            let p4x = this.timeToCoordinate(p4.x)
            let p3y = this.priceToCoordinate(p3.y)
            let p4y = this.priceToCoordinate(p4.y)
            if (p3x && p4x && p3y && p4y) {
                this.scaledCoordinates.p3x = scaledCoordinate(p3x, this.horizontalPixelRatio) as Coordinate
                this.scaledCoordinates.p3y = scaledCoordinate(p3y, this.verticalPixelRatio) as Coordinate
                this.scaledCoordinates.p4x = scaledCoordinate(p4x, this.horizontalPixelRatio) as Coordinate
                this.scaledCoordinates.p4y = scaledCoordinate(p4y, this.verticalPixelRatio) as Coordinate
            }
        }

        const p1Index = this.timeToLogical(p1.x) || 0
        const p2Index = this.timeToLogical(p2.x) || 0

        const minIndex = Math.min(p1Index, p2Index), maxIndex = Math.max(p1Index, p2Index)
        this.bars = maxIndex - minIndex + 1

        const data = this.getSeries().data()
        const dataLength = data.length
        const emptySlots = this._chart.emptyTimeSlots
        let lastDateIndex: number = -1
        let lastDate: string = ''
        let numOfDays = -1
        for (let c = minIndex; c <= maxIndex; c++) {
            let candle = data[c] || emptySlots[c - dataLength]
            let currentDate = toDayFormat(istToUTC(candle.time as number) * 1000)
            if (lastDate !== currentDate) {
                lastDateIndex = c
                lastDate = currentDate
                numOfDays += 1
                continue
            }
        }
        const numOfBars = maxIndex - lastDateIndex + 1
        const timeBucket = this._chart.chartConfig.timeBucket

        this.days = numOfDays
        let timeWidth = numOfBars * getTimeWidthInMilliSeconds(timeBucket)

        this.hours = Math.floor(timeWidth / 3600000)
        timeWidth = timeWidth - this.hours * 3600000
        this.minutes = Math.floor(timeWidth / 60000)
        this.seconds = (timeWidth - this.minutes * 60000) / 1000

    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    recalculateHandles = () => {
        const { p1, p2 } = this._state

        if (!p1 || !p2) return

        this._state.p3 = {
            id: 'p3',
            x: p1.x,
            y: p2.y
        }
        this._state.p4 = {
            id: 'p4',
            y: p1!.y,
            x: p2.x
        }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1x, p2x, p1y, p2y } = this.scaledCoordinates
        const minX = Math.min(p1x, p2x)
        const maxX = Math.max(p1x, p2x)
        const minY = Math.min(p1y, p2y)
        const maxY = Math.max(p1y, p2y)
        if (scaledX < minX || scaledX > maxX || scaledY < minY || scaledY > maxY) return null

        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
                case 'p3':
                    this._state.p3 = h
                    break
                case 'p4':
                    this._state.p4 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {

        switch (id) {
            case 'p1':
                this._state.p1!.x = time
                this._state.p1!.y = price

                this._state.p3!.x = time
                this._state.p4!.y = price
                break
            case 'p2':
                this._state.p2!.x = time
                this._state.p2!.y = price

                this._state.p4!.x = time
                this._state.p3!.y = price
                break
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        return []

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => (a.x as number) - (b.x as number))

        return [{
            id: 'time-outer',
            p1: sorted[0].x,
            p2: sorted[1].x,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<DateRangeDrawingSettings, SettingValues>[] => {
        return [
            {
                id: 'style',
                displayName: 'Style',
                visible: true,
                settings: [
                    {
                        name: 'fillColor',
                        label: 'Fill Color',
                        type: SettingType.COLOR,
                        defaultValue: {
                            color: '#c2deff',
                            opacity: 0.3
                        },
                        custom: true,
                        visible: true
                    },
                    {
                        name: 'labelColor',
                        label: 'Label Color',
                        type: SettingType.COLOR,
                        defaultValue: {
                            color: '#6397d4',
                            opacity: 0.7
                        },
                        custom: true,
                        visible: true
                    },
                    {
                        name: 'strokeColor',
                        label: 'Border Color',
                        type: SettingType.COLOR,
                        defaultValue: { color: '#3876EE', opacity: 0.5 },
                        custom: true,
                        visible: true
                    },
                    {
                        name: 'strokeWidth',
                        label: 'Border Width',
                        type: SettingType.STROKE_WIDTH,
                        defaultValue: 2,
                        custom: true,
                        visible: true
                    }
                ]
            }
        ]
    }

    getSettingsValues(): DateRangeDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof DateRangeDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'dateRange'
    }
}

export default DateRangeDrawingTool